import React, { useEffect, useState } from 'react';
import { productsAPI, categoriesAPI } from '../services/api';
import { Product, Category } from '../types';
import { useAuth } from '../contexts/AuthContext';
import ProductAttrs from '../components/ProductAttrs';
import './Products.css';

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [formData, setFormData] = useState({
    categoryId: '',
    name: '',
    unit: 'PIECE' as 'PIECE' | 'METER' | 'KG',
    price: '',
    costPrice: '',
    attributes: [] as Array<{ key: string; value: string }>,
  });
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 0 });
  const [showDeleted, setShowDeleted] = useState(false);
  const { isAdmin } = useAuth();

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, [page, search, categoryFilter, showDeleted]);

  const fetchCategories = async () => {
    try {
      const data = await categoriesAPI.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const params: any = { page, limit: 10 };
      if (search) params.search = search;
      if (categoryFilter) params.categoryId = parseInt(categoryFilter);
      if (showDeleted) params.withDeleted = true;
      const data = await productsAPI.getAll(params);
      setProducts(data.data);
      setMeta(data.meta);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const productData: any = {
        categoryId: parseInt(formData.categoryId),
        name: formData.name,
        unit: formData.unit,
        price: formData.price ? parseFloat(formData.price) : 0,
        costPrice: formData.costPrice ? parseFloat(formData.costPrice) : 0,
        attributes: formData.attributes.filter(
          (attr) => attr.key.trim() && attr.value.trim()
        ),
      };
      if (editing) {
        await productsAPI.update(editing.id, productData);
      } else {
        await productsAPI.create(productData);
      }
      setShowForm(false);
      setEditing(null);
      setFormData({ categoryId: '', name: '', unit: 'PIECE', price: '', costPrice: '', attributes: [] });
      fetchProducts();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при сохранении');
    }
  };

  const handleEdit = (product: Product) => {
    setEditing(product);
    setFormData({
      categoryId: product.categoryId.toString(),
      name: product.name,
      unit: product.unit,
      price: product.price != null ? String(product.price) : '',
      costPrice: product.costPrice != null ? String(product.costPrice) : '',
      attributes: product.attributes
        ? product.attributes.map((attr) => ({ key: attr.key, value: attr.value }))
        : [],
    });
    setShowForm(true);
  };

  const addAttribute = () => {
    setFormData({
      ...formData,
      attributes: [...formData.attributes, { key: '', value: '' }],
    });
  };

  const removeAttribute = (index: number) => {
    setFormData({
      ...formData,
      attributes: formData.attributes.filter((_, i) => i !== index),
    });
  };

  const updateAttribute = (index: number, field: 'key' | 'value', value: string) => {
    const newAttributes = [...formData.attributes];
    newAttributes[index] = { ...newAttributes[index], [field]: value };
    setFormData({ ...formData, attributes: newAttributes });
  };

  const handleDelete = async (product: Product) => {
    if (!window.confirm(`Удалить товар "${product.name}"?`)) return;
    try {
      await productsAPI.delete(product.id);
      fetchProducts();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при удалении');
    }
  };

  const handleRestore = async (product: Product) => {
    if (!window.confirm(`Восстановить товар "${product.name}"?`)) return;
    try {
      await productsAPI.restore(product.id);
      fetchProducts();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при восстановлении');
    }
  };

  if (loading) {
    return <div>Загрузка...</div>;
  }

  return (
    <div className="products">
      <div className="page-header">
        <h1>Товары</h1>
        {isAdmin() && (
          <button onClick={() => setShowForm(!showForm)} className="btn-primary">
            {showForm ? 'Отмена' : '+ Добавить товар'}
          </button>
        )}
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Поиск товаров..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="search-input"
        />
        <select
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
          className="filter-select"
        >
          <option value="">Все категории</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
        {isAdmin() && (
          <button
            className={`btn-toggle-deleted ${showDeleted ? 'active' : ''}`}
            onClick={() => { setShowDeleted(!showDeleted); setPage(1); }}
          >
            🗑 {showDeleted ? 'Скрыть удалённые' : 'Показать удалённые'}
          </button>
        )}
      </div>

      {showForm && isAdmin() && (
        <div className="form-card">
          <h2>{editing ? 'Редактировать товар' : 'Новый товар'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Категория</label>
              <select
                value={formData.categoryId}
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                required
              >
                <option value="">Выберите категорию</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Название</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            {isAdmin() && (
              <div className="form-group">
                <label>Себестоимость (сом)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.costPrice}
                  onChange={(e) => setFormData({ ...formData, costPrice: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            )}
            <div className="form-group">
              <label>Цена продажи (сом)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div className="form-group">
              <label>Единица измерения</label>
              <select
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value as any })}
                required
              >
                <option value="PIECE">Штука</option>
                <option value="METER">Метр</option>
                <option value="KG">Кг</option>
              </select>
            </div>
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label>Характеристики</label>
                <button type="button" onClick={addAttribute} className="btn-secondary" style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}>
                  + Добавить
                </button>
              </div>
              {formData.attributes.map((attr, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    placeholder="Параметр (напр. диаметр)"
                    value={attr.key}
                    onChange={(e) => updateAttribute(index, 'key', e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <input
                    type="text"
                    placeholder="Значение (напр. 15)"
                    value={attr.value}
                    onChange={(e) => updateAttribute(index, 'value', e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button type="button" onClick={() => removeAttribute(index)} className="btn-delete" style={{ padding: '0.5rem' }}>
                    ×
                  </button>
                </div>
              ))}
              {formData.attributes.length === 0 && (
                <p style={{ color: '#7f8c8d', fontStyle: 'italic' }}>
                  Характеристики не добавлены.
                </p>
              )}
            </div>
            <button type="submit" className="btn-primary">
              {editing ? 'Сохранить' : 'Создать'}
            </button>
          </form>
        </div>
      )}

      {/* Desktop Products Table */}
      <div className="table-container products-desktop-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Название</th>
              <th>Категория</th>
              {isAdmin() && <th>Себест.</th>}
              <th>Цена</th>
              <th>Ед. изм.</th>
              <th>Характеристики</th>
              <th>Склад</th>
              {isAdmin() && <th>Действия</th>}
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id} className={product.deletedAt ? 'row-deleted' : ''}>
                <td>{product.id}</td>
                <td>{product.name}</td>
                <td>{product.category?.name || '-'}</td>
                {isAdmin() && <td>{Number(product.costPrice).toLocaleString('ru-RU')} сом</td>}
                <td>{Number(product.price).toLocaleString('ru-RU')} сом</td>
                <td>{product.unit === 'PIECE' ? 'Штука' : product.unit === 'METER' ? 'Метр' : 'Кг'}</td>
                <td>
                  <ProductAttrs attributes={product.attributes} mode="full" />
                </td>
                <td>{product.stock?.quantity || 0}</td>
                {isAdmin() && (
                  <td>
                    <div className="action-buttons-cell">
                      {!product.deletedAt && (
                        <button onClick={() => handleEdit(product)} className="btn-edit">Изменить</button>
                      )}
                      {product.deletedAt ? (
                        <button onClick={() => handleRestore(product)} className="btn-restore">Восстановить</button>
                      ) : (
                        <button onClick={() => handleDelete(product)} className="btn-delete">Удалить</button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Products Cards */}
      <div className="products-mobile-list">
        {products.length === 0 ? (
          <div className="mobile-empty-card">Товары не найдены</div>
        ) : (
          products.map((product) => (
            <div key={product.id} className={`mobile-product-card ${product.deletedAt ? 'card-deleted' : ''}`}>
              <div className="mobile-card-head">
                <div className="mobile-prod-title-col">
                  <span className="mobile-prod-id">#{product.id}</span>
                  <span className="mobile-prod-name">{product.name}</span>
                  {product.category?.name && (
                    <span className="mobile-cat-badge">{product.category.name}</span>
                  )}
                </div>
                <div className="mobile-prod-price-badge">
                  {Number(product.price).toLocaleString('ru-RU')} сом
                </div>
              </div>

              {product.attributes && product.attributes.length > 0 && (
                <div className="mobile-card-attrs">
                  <ProductAttrs attributes={product.attributes} mode="full" />
                </div>
              )}

              <div className="mobile-card-details">
                {isAdmin() && (
                  <div className="mobile-detail-row">
                    <span className="mobile-detail-label">Себестоимость:</span>
                    <span className="mobile-detail-val">{Number(product.costPrice).toLocaleString('ru-RU')} сом</span>
                  </div>
                )}
                <div className="mobile-detail-row">
                  <span className="mobile-detail-label">Единица:</span>
                  <span className="mobile-detail-val">{product.unit === 'PIECE' ? 'Штука' : product.unit === 'METER' ? 'Метр' : 'Кг'}</span>
                </div>
                <div className="mobile-detail-row">
                  <span className="mobile-detail-label">Остаток на складе:</span>
                  <span className="mobile-detail-val" style={{ fontWeight: 700, color: (product.stock?.quantity ?? 0) <= 0 ? '#ef4444' : '#1e293b' }}>
                    {product.stock?.quantity || 0} {product.unit === 'PIECE' ? 'шт.' : product.unit === 'METER' ? 'м.' : 'кг.'}
                  </span>
                </div>
              </div>

              {isAdmin() && (
                <div className="mobile-card-footer-actions">
                  {!product.deletedAt && (
                    <button onClick={() => handleEdit(product)} className="btn-edit">Изменить</button>
                  )}
                  {product.deletedAt ? (
                    <button onClick={() => handleRestore(product)} className="btn-restore">Восстановить</button>
                  ) : (
                    <button onClick={() => handleDelete(product)} className="btn-delete">Удалить</button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="pagination">
        <button onClick={() => setPage(page - 1)} disabled={page === 1} className="btn-primary">
          ← Назад
        </button>
        <span>Страница {page} из {meta.totalPages}</span>
        <button onClick={() => setPage(page + 1)} disabled={page >= meta.totalPages} className="btn-primary">
          Вперёд →
        </button>
      </div>
    </div>
  );
};

export default Products;
